/**
 * spiritualLanguageFilter.js
 *
 * Prevents spiritual clichés and auto-spiritualization in Kevia responses.
 *
 * DISTINCT from pastoralToneGuide.FORBIDDEN_PATTERNS (which targets corporate/AI jargon)
 * and responseBoundaryEngine (which targets ethical safety violations).
 * This filter specifically targets:
 *   1. Christian clichés that sound hollow
 *   2. Auto-spiritualization — inserting scripture/prayer where not asked
 */

// These are stock phrases that rob pastoral moments of authenticity.
// Named explicitly so the LLM understands what to avoid, not just "be authentic."
const SPIRITUAL_CLICHÉS = [
  "Dios tiene un propósito para todo",
  "todo pasa por algo",
  "solo ten fe",
  "confía en Dios y todo estará bien",
  "Dios nunca da más de lo que puedes soportar",
  "es la voluntad de Dios",
  "Dios lo permite para enseñarte",
  "pon todo en las manos de Dios",
  "todo en el tiempo de Dios",
  "Dios tiene el control",
];

// Questions that are operational in nature — the LLM should NOT insert
// spiritual content unless it's genuinely relevant.
const OPERATIONAL_QUESTION_PATTERNS = [
  /¿.*visita[s]?\s+(?:tengo|hay|quedan|pendientes)/i,
  /¿.*(?:cuántos|cuántas)\s+(?:personas|familias|iglesias|eventos)/i,
  /¿.*agenda\s+(?:de\s+)?(?:hoy|esta\s+semana|mañana)/i,
  /¿.*(?:qué\s+tengo|qué\s+sigue|qué\s+hay)\s+(?:hoy|mañana|esta\s+semana)/i,
  /¿.*próximo\s+evento/i,
];

/**
 * @param {{
 *   text:    string,
 *   intent:  string,
 *   emotion: object|null,
 * }} params
 * @returns {{ hasFilter: boolean, instruction: string|null }}
 */
export function buildSpiritualLanguageGuidance({ text, intent, emotion }) {
  const hasSpiritualEmotion =
    ["spiritual_confusion", "grief", "discouragement", "fear", "shame"]
      .includes(emotion?.dominantEmotion);

  const isOperationalQuestion =
    OPERATIONAL_QUESTION_PATTERNS.some((p) => p.test(text ?? "")) &&
    !hasSpiritualEmotion;

  // Operational question with no emotional overlay → don't spiritualize
  if (isOperationalQuestion) {
    return {
      hasFilter: true,
      instruction:
        "No insertes contenido espiritual donde no fue pedido. Responde la pregunta directamente.",
    };
  }

  // Emotional/spiritual context — avoid hollow clichés
  if (hasSpiritualEmotion) {
    const examples = SPIRITUAL_CLICHÉS.slice(0, 3).join('", "');
    return {
      hasFilter: true,
      instruction: `Evita clichés espirituales como: "${examples}"… Habla desde autenticidad pastoral.`,
    };
  }

  return { hasFilter: false, instruction: null };
}
